import { useState } from "react";
import { Ellipsis, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import ACL_RELATIONSHIPS from "acl-relationships";
import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Authorized from "components/Authorized";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import FormDialog from "components/modals/FormDialog";
import { invalidateMachineApprovalStatusTableQuery } from "hooks/useMachineApprovalStatusTable";
import MachineApprovalStatusForm from "./MachineApprovalStatusForm";
import MachineApprovalStatusService from "services/MachineApprovalStatus";

const EDIT_ACLS = [ACL_RELATIONSHIPS.MachineApprovalStatus.edit];
const DELETE_ACLS = [ACL_RELATIONSHIPS.MachineApprovalStatus.delete];
const ANY_ACLS = [...EDIT_ACLS, ...DELETE_ACLS];

export function MachineApprovalStatusTableActions({ original }) {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleDelete = async () => {
    try {
      await MachineApprovalStatusService.delete(original.id);
      toast.success("Machine Approval Status deleted");
      setDeleteOpen(false);
      await invalidateMachineApprovalStatusTableQuery();
    } catch {
      toast.error("Failed to delete Machine Approval Status");
    }
  };

  return (
    <Authorized permissions={ANY_ACLS} partialMatch>
      <>
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="Machine approval status actions"
            className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
          >
            <Ellipsis />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <Authorized permissions={EDIT_ACLS}>
              <DropdownMenuItem onClick={() => setEditOpen(true)}>
                <Pencil />
                Edit
              </DropdownMenuItem>
            </Authorized>
            <Authorized permissions={DELETE_ACLS}>
              <DropdownMenuItem
                variant="destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 />
                Delete
              </DropdownMenuItem>
            </Authorized>
          </DropdownMenuContent>
        </DropdownMenu>
        <FormDialog
          FormComponent={MachineApprovalStatusForm}
          model={original}
          modelType="Machine Approval Status"
          open={editOpen}
          onOpenChange={setEditOpen}
          onSuccess={invalidateMachineApprovalStatusTableQuery}
        />
        <ConfirmDeleteDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete Machine Approval Status?"
          description="Are you sure you want to delete this Machine Approval Status?"
          onConfirm={handleDelete}
        />
      </>
    </Authorized>
  );
}
